/**
 * Storefront environment, validated at module load so a missing key fails the
 * build loudly rather than shipping `undefined` into a fetch URL.
 *
 * Hand-written rather than Zod on purpose: this validates six strings once at
 * boot, and Zod is roughly 14 KB gzipped in an entry bundle the PRD budgets at
 * 200 KB. Zod is still used for forms, which live on lazily-loaded routes where
 * its cost is paid only by the people who reach them.
 *
 * Only VITE_-prefixed keys reach the browser. The Razorpay secret never appears
 * here by design — only the public key id does.
 */

const problems = [];

const read = (key, { required = true, url = false } = {}) => {
  const value = import.meta.env[key];

  if (value === undefined || value === '') {
    if (required) problems.push(`  ${key} — required`);
    return '';
  }

  if (url && !/^https?:\/\/[^\s]+$/.test(value)) {
    problems.push(`  ${key} — must be an http(s) URL, got "${value}"`);
    return '';
  }

  return String(value).replace(/\/$/, '');
};

const apiUrl = read('VITE_API_URL', { url: true });
const uploadUrl = read('VITE_PUBLIC_UPLOAD_URL', { url: true });
const razorpayKeyId = read('VITE_RAZORPAY_KEY_ID');
const siteUrl = read('VITE_SITE_URL', { url: true });
const sentryDsn = read('VITE_SENTRY_DSN', { required: false });
const gaId = read('VITE_GA_ID', { required: false });
const googleMapsApiKey = read('VITE_GOOGLE_MAPS_API_KEY', { required: false });

if (problems.length > 0) {
  throw new Error(`Storefront environment is not usable:\n${problems.join('\n')}`);
}

export const env = Object.freeze({
  apiUrl,
  uploadUrl,
  razorpayKeyId,
  siteUrl,
  sentryDsn,
  gaId,
  googleMapsApiKey,
  isProduction: import.meta.env.PROD,
});

export default env;
